import React, { memo, useCallback, useState } from 'react'

import {
  AttentionFilled,
  Button,
  Icon,
  TextInput,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func } from 'prop-types'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'

import { NOOP } from 'Utils/stableDefaults'

const ACCESS_CODE_PATH = '/api/internal/generator/access-code'

const ACCESS_CODE_SUCCESS_MESSAGE = "Setting up your access -- we'll notify you once it's ready."
const ACCESS_CODE_INVALID_MESSAGE = 'Invalid access code. Please try again.'
const ACCESS_CODE_ERROR_MESSAGE = 'Could not validate the access code. Please try again.'

// Generic access-code entry shared by the 3D Studio usage modal and the AI subscription
// landing waitlist modal. The component owns the POST, the success/error toasts, and the
// inline validation UI; the caller supplies onApplied to persist the (app-specific) result --
// the studio writes it to its store, the landing has nothing to persist.
function AccessCodeForm({ onApplied = NOOP, onClose: handleClose }) {
  const { postData: postAccessCode, isLoading: isApplying } = useApiPost()
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState(null)

  const handleCodeChange = useCallback((event) => {
    setCode(event.target.value)
    setCodeError(prev => (prev ? null : prev))
  }, [])

  const handleApplyCode = useCallback(() => {
    const trimmed = code.trim()

    if (!trimmed || isApplying) {
      return
    }

    postAccessCode({
      path: ACCESS_CODE_PATH,
      data: { code: trimmed },
      onSuccess: (response) => {
        const data = response?.data?.data || {}

        onApplied(data)
        window.showNotification(ACCESS_CODE_SUCCESS_MESSAGE, { type: 'info', autohide: true })
        handleClose()
      },
      onError: (error) => {
        const isInvalid = error?.response?.data?.error === 'invalid_code'
        if (isInvalid) {
          setCodeError(ACCESS_CODE_INVALID_MESSAGE)
        } else {
          window.showNotification(ACCESS_CODE_ERROR_MESSAGE, { type: 'error', autohide: true })
        }
      },
    })
  }, [code, isApplying, postAccessCode, onApplied, handleClose])

  return (
    <div className="usage-modal__access-code-form">
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label
        htmlFor="usage-modal-access-code-input"
        className="usage-modal__access-code-label cgt-body-base-semibold"
      >
        Enter the code for free beta access
      </label>
      <TextInput
        id="usage-modal-access-code-input"
        size="medium"
        placeholder="Enter your code"
        value={code}
        onChange={handleCodeChange}
        isDisabled={isApplying}
        isWarning={!!codeError}
        textInputClassName="usage-modal__access-code-input"
        aria-invalid={codeError ? 'true' : 'false'}
        aria-describedby={codeError ? 'usage-modal-access-code-error' : undefined}
        trailButton={(
          <Button
            size="small"
            type="primary"
            customClass="usage-modal__access-code-apply"
            onClick={handleApplyCode}
            disabled={isApplying || !code.trim()}
          >
            {isApplying ? 'Applying...' : 'Apply'}
          </Button>
        )}
        isPilot
      />
      {codeError && (
        <div className="usage-modal__access-code-error-row">
          <Icon
            color="warning"
            notSpaced
            size="small"
            svgComponent={AttentionFilled}
          />
          <p
            id="usage-modal-access-code-error"
            className="usage-modal__access-code-error cgt-body-base"
            role="alert"
          >
            {codeError}
          </p>
        </div>
      )}
    </div>
  )
}

AccessCodeForm.propTypes = {
  onApplied: func,
  onClose: func.isRequired,
}

export default memo(AccessCodeForm)
